const MIME_EXTENSION_MAP: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/svg+xml': 'svg',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'application/pdf': 'pdf',
  'text/plain': 'txt',
};

function getExtensionFromName(name: string): string {
  const trimmed = name.trim();
  const dotIndex = trimmed.lastIndexOf('.');
  if (dotIndex <= 0 || dotIndex === trimmed.length - 1) return '';
  return trimmed.slice(dotIndex + 1).toLowerCase();
}

function inferExtension(type: string, name: string): string {
  const fromName = getExtensionFromName(name);
  if (fromName) return fromName;
  return MIME_EXTENSION_MAP[type] || '';
}

function buildClipboardFileName(file: File, index: number): string {
  const originalName = file.name?.trim() || '';
  if (originalName && getExtensionFromName(originalName)) {
    return originalName;
  }

  const prefix = file.type.startsWith('image/') ? 'pasted-image' : 'pasted-file';
  const stamp = new Date().toISOString().replace(/\D/g, '').slice(0, 14);
  const extension = inferExtension(file.type, originalName);
  return extension
    ? `${prefix}-${stamp}-${index + 1}.${extension}`
    : `${prefix}-${stamp}-${index + 1}`;
}

function normalizeClipboardFile(file: File, index: number): File {
  const normalizedName = buildClipboardFileName(file, index);
  if (file.name === normalizedName) return file;

  try {
    return new File([file], normalizedName, {
      type: file.type || 'application/octet-stream',
      lastModified: file.lastModified || Date.now(),
    });
  } catch {
    return file;
  }
}

export function extractClipboardFiles(dataTransfer: DataTransfer | null): File[] {
  if (!dataTransfer) return [];

  const candidates: File[] = [];

  if (dataTransfer.items && dataTransfer.items.length > 0) {
    for (const item of Array.from(dataTransfer.items)) {
      if (item.kind !== 'file') continue;
      const file = item.getAsFile();
      if (file) candidates.push(file);
    }
  }

  if (candidates.length === 0 && dataTransfer.files && dataTransfer.files.length > 0) {
    candidates.push(...Array.from(dataTransfer.files));
  }

  const normalized = candidates.map((file, index) => normalizeClipboardFile(file, index));
  const seen = new Set<string>();

  return normalized.filter((file) => {
    const key = `${file.name}:${file.type}:${file.size}:${file.lastModified}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
